import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useForgotPasswordMutation } from '../redux/features/api';
import { useValidationForm } from './useValidationForm';
import { useRecaptcha } from './useRecaptcha';
import { APP_PATH_LOGIN } from '../config';
import {
  trackForgotPasswordError,
  trackForgotPasswordSubmit,
  trackForgotPasswordSuccess
} from '../helpers';

const schema = {
  email: {
    presence: { allowEmpty: false, message: 'is required' },
    email: true,
    length: {
      maximum: 64
    }
  }
};

export const useForgotPassword = () => {
  const history = useHistory();
  const [forgotPassword, { isLoading, isSuccess }] =
    useForgotPasswordMutation();
  const { executeRecaptcha } = useRecaptcha();

  const { formState, handleChange, hasError } = useValidationForm({
    schemaToValidate: schema
  });

  const handleBack = useCallback(() => {
    history.push(APP_PATH_LOGIN);
  }, [history]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      try {
        // Track forgot password form submission
        trackForgotPasswordSubmit(formState.values.email);

        // Get reCAPTCHA token
        const recaptchaToken = await executeRecaptcha('forgot_password');

        // Call the forgot password API
        await forgotPassword({
          email: formState.values.email,
          recaptchaToken
        }).unwrap();

        // Track success
        trackForgotPasswordSuccess();
      } catch (error) {
        console.error('Forgot password error:', error);

        // Track error with sanitized message
        const errorMessage =
          error?.data?.message || error?.message || 'unknown_error';
        trackForgotPasswordError(errorMessage);
      }
    },
    [formState.values.email, forgotPassword, executeRecaptcha]
  );

  return {
    formState,
    handleChange,
    handleBack,
    handleSubmit,
    hasError,
    isLoading,
    isSuccess
  };
};
